import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import BarChartIcon from "@mui/icons-material/BarChart";
import PieChartIcon from "@mui/icons-material/PieChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import dayjs, { Dayjs } from "dayjs";
import { ModalityInsightType, CourtTypes } from "../../types/generalTypes";
import { useModalityInsights } from "../../hooks/useInsights";
import { getCourtsByCompany } from "../../api/CourtAPI";
import { getCompanyData } from "../../utils/generalUtil";
import LoadingSpinner from "../loadingSpinner/LoadingSpinner";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

type ChartType = "bar" | "line" | "pie";

const MODALITY_COLORS = [
  "#226FE2",
  "#E91E63",
  "#9C27B0",
  "#FF9800",
  "#4CAF50",
  "#00BCD4",
  "#FF5722",
  "#795548",
];

const getModalityColor = (index: number): string => {
  return MODALITY_COLORS[index % MODALITY_COLORS.length];
};

const getModalityDisplayName = (modality: string): string => {
  const modalityNames: Record<string, string> = {
    BeachTennis: "Beach Tennis",
    Futvolei: "Futvôlei",
    Volei: "Vôlei",
    "0": "Beach Tennis",
    "1": "Futvôlei",
    "2": "Vôlei",
  };
  return modalityNames[modality] || modality;
};

const ModalityInsights = () => {
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs().endOf("month"));
  const [selectedCourts, setSelectedCourts] = useState<number[]>([]);
  const [courts, setCourts] = useState<CourtTypes[]>([]);
  const [modalityInsights, setModalityInsights] = useState<
    ModalityInsightType[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [chartType, setChartType] = useState<ChartType>("bar");

  const { fetchModalityInsights } = useModalityInsights({
    setModalityInsights,
    setIsLoading,
  });

  useEffect(() => {
    const loadCourts = async () => {
      try {
        const companyData = getCompanyData();
        const courtsData = await getCourtsByCompany(
          companyData?.companyId || 0,
        );
        setCourts(courtsData);
        setSelectedCourts(courtsData.map((c) => Number(c.courtId)));
      } catch (error) {
        console.error("Failed to load courts:", error);
      }
    };

    loadCourts();
  }, []);

  const handleFetchInsights = useCallback(async () => {
    if (selectedCourts.length === 0) {
      alert("Selecione pelo menos uma quadra");
      return;
    }

    const companyData = getCompanyData();

    await fetchModalityInsights({
      companyId: companyData?.companyId || 0,
      courtIds: selectedCourts,
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    });
    setHasLoaded(true);
  }, [selectedCourts, startDate, endDate, fetchModalityInsights, setHasLoaded]);

  useEffect(() => {
    if (selectedCourts.length > 0 && !hasLoaded) {
      handleFetchInsights();
      setHasLoaded(true);
    }
  }, [selectedCourts, hasLoaded, handleFetchInsights]);

  const handleChartTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newChartType: ChartType,
  ) => {
    if (newChartType !== null) {
      setChartType(newChartType);
    }
  };

  const getTotalReservations = () => {
    return modalityInsights.reduce(
      (sum, modality) => sum + modality.reservationCount,
      0,
    );
  };

  const getMostPopularModality = (): ModalityInsightType | null => {
    if (modalityInsights.length === 0) return null;
    return modalityInsights.reduce((max, modality) =>
      modality.reservationCount > max.reservationCount ? modality : max,
    );
  };

  const getLeastPopularModality = (): ModalityInsightType | null => {
    if (modalityInsights.length === 0) return null;
    return modalityInsights.reduce((min, modality) =>
      modality.reservationCount < min.reservationCount ? modality : min,
    );
  };

  const prepareChartData = () => {
    return modalityInsights.map((item, index) => ({
      name: getModalityDisplayName(item.modality),
      value: item.reservationCount,
      percentage: item.percentage,
      fill: getModalityColor(index),
    }));
  };

  const prepareTimeSeriesData = () => {
    const data = modalityInsights.map((item, index) => ({
      name: getModalityDisplayName(item.modality),
      reservas: item.reservationCount,
      fill: getModalityColor(index),
    }));
    return data;
  };

  const mostPopular = getMostPopularModality();
  const leastPopular = getLeastPopularModality();

  const renderBarChart = () => {
    const data = prepareChartData();

    return (
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis dataKey="name" stroke="#666" tick={{ fill: "#666" }} />
        <YAxis stroke="#666" tick={{ fill: "#666" }} />
        <Tooltip />
        <Legend />
        <Bar dataKey="value" name="Reservas">
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.fill} />
          ))}
        </Bar>
      </BarChart>
    );
  };

  const renderLineChart = () => {
    const data = prepareTimeSeriesData();

    return (
      <LineChart data={data} layout="vertical">
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis type="number" stroke="#666" tick={{ fill: "#666" }} />
        <YAxis
          dataKey="name"
          type="category"
          stroke="#666"
          tick={{ fill: "#666" }}
          width={100}
        />
        <Tooltip />
        <Legend />
        <Line
          dataKey="reservas"
          name="Reservas"
          stroke="#226FE2"
          strokeWidth={2}
        />
      </LineChart>
    );
  };

  const renderPieChart = () => {
    const data = prepareChartData();

    return (
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={(entry: any) =>
            `${entry.name}: ${entry.percentage.toFixed(1)}%`
          }
          outerRadius={100}
          fill="#8884d8"
          dataKey="value"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.fill} />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <LoadingSpinner />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" fontWeight="bold">
          Insights por Modalidade
        </Typography>
        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={handleChartTypeChange}
          size="small"
        >
          <ToggleButton value="bar" aria-label="bar chart">
            <BarChartIcon />
          </ToggleButton>
          <ToggleButton value="line" aria-label="line chart">
            <ShowChartIcon />
          </ToggleButton>
          <ToggleButton value="pie" aria-label="pie chart">
            <PieChartIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Data Início"
                value={startDate}
                onChange={(newValue) => newValue && setStartDate(newValue)}
                maxDate={endDate}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Data Fim"
                value={endDate}
                onChange={(newValue) => newValue && setEndDate(newValue)}
                minDate={startDate}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Quadras</InputLabel>
              <Select
                multiple
                value={selectedCourts}
                label="Quadras"
                onChange={(e) => setSelectedCourts(e.target.value as number[])}
                renderValue={(selected) =>
                  `${selected.length} quadra(s) selecionada(s)`
                }
              >
                {courts.map((court) => (
                  <MenuItem key={court.courtId} value={Number(court.courtId)}>
                    <Checkbox
                      checked={selectedCourts.includes(Number(court.courtId))}
                    />
                    <ListItemText primary={court.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleFetchInsights}
              disabled={selectedCourts.length === 0}
            >
              Buscar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {modalityInsights.length > 0 && (
        <>
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom fontSize={14}>
                    Total de Reservas
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {getTotalReservations()}
                  </Typography>
                  <Typography color="textSecondary" fontSize={12} mt={1}>
                    {startDate.format("MMM DD")} - {endDate.format("MMM DD")}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom fontSize={14}>
                    Modalidade Mais Popular
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {mostPopular
                      ? getModalityDisplayName(mostPopular.modality)
                      : "-"}
                  </Typography>
                  {mostPopular && (
                    <Typography color="textSecondary" fontSize={12} mt={1}>
                      {mostPopular.reservationCount} reservas (
                      {mostPopular.percentage.toFixed(1)}%)
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom fontSize={14}>
                    Modalidade Menos Popular
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {leastPopular
                      ? getModalityDisplayName(leastPopular.modality)
                      : "-"}
                  </Typography>
                  {leastPopular && (
                    <Typography color="textSecondary" fontSize={12} mt={1}>
                      {leastPopular.reservationCount} reservas (
                      {leastPopular.percentage.toFixed(1)}%)
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    Comparação por Modalidade
                  </Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    {chartType === "pie" ? renderPieChart() : renderBarChart()}
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    Distribuição de Reservas
                  </Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    {renderLineChart()}
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Detalhamento por Modalidade
            </Typography>
            <Grid container spacing={2}>
              {modalityInsights.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={item.modality}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderLeft: `4px solid ${getModalityColor(index)}`,
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold">
                        {getModalityDisplayName(item.modality)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" mt={1}>
                        Reservas: <strong>{item.reservationCount}</strong>
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Participação:{" "}
                        <strong>{item.percentage.toFixed(1)}%</strong>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </>
      )}

      {modalityInsights.length === 0 && hasLoaded && (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="textSecondary">
            Nenhuma reserva encontrada para o período selecionado
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ModalityInsights;
